import React, { useMemo } from 'react';
import { t } from '@lingui/macro';
import styled from 'styled-components';
import useSentinelTeam from 'hooks/useSentinelTeam';

import Badge from '@decisiv/ui-components/lib/components/Badge';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { H3 } from '@decisiv/ui-components/lib/components/Typography';
import Briefcase from '@decisiv/iconix/lib/components/Briefcase';

import ROUTE_NAMES from 'setup/Routing/routeNames';

import Card from 'components/DashboardCard';

import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';

import Loading from 'components/Loading';
import Divider from 'components/Divider';

import isNull from 'lodash/isNull';
import useTeamNavigate from 'hooks/useTeamNavigate';
import { useAssignedToMe } from '../../AssignedToMeContext';
import { Grid } from './styles';
import LinkOrLabel from '../LinkOrLabel';

const dashboardScheduledMaintenanceByStatusQuery = loader(
  './dashboardScheduledMaintenanceByStatus.graphql',
);

const Paper = styled(Flex).attrs({ padding: 2 })`
  width: 100%;
  box-shadow: 0px 1px 4px rgba(37, 38, 46, 0.4);
  border-radius: 2px;
`;

const getCountText = (loading, total) => {
  /* istanbul ignore next -- @preserve */
  if (loading) return '';

  return isNull(total) ? /* istanbul ignore next -- @preserve */ '―' : total;
};

const getBadgeColor = (total) => {
  return isNull(total)
    ? /* istanbul ignore next -- @preserve */ 'licoriceMousse'
    : 'information';
};

const infoText = t`View the total Scheduled Maintenance alerts organized by due or overdue. Click the panel links to see the assets with those Scheduled Maintenance alerts. Note that the number indicates the alerts, not the assets.`;

export default function ScheduledMaintenanceCard() {
  const navigate = useTeamNavigate();
  const { customerIds, buildFilteredPath } = useAssignedToMe();

  const {
    loading,
    data: {
      dashboardScheduledMaintenanceByStatus: { total, due, overdue } = {},
    } = {},
  } = useQuery(dashboardScheduledMaintenanceByStatusQuery, {
    variables: { customerIds },
  });

  const { alertConfiguration } = useSentinelTeam();

  // The button is only enabled if the total is not null and the alert is enabled
  const isViewAllDisabled = useMemo(() => {
    const scheduledMaintenanceAlert = alertConfiguration.filter(
      (alert) => alert.id === 'ScheduledMaintenance',
    );

    return !total && !scheduledMaintenanceAlert[0].enabled;
  }, [alertConfiguration, total]);

  const onClickViewAll = () =>
    navigate(
      buildFilteredPath(
        `${ROUTE_NAMES.alerts}?alertTypes=preventative_maintenance`,
      ),
    );

  const scheduledMaintenanceAlertsLabel = t`Scheduled Maintenance Alerts`;
  const scheduledMaintenanceDueLabel = t`Due`;
  const scheduledMaintenanceOverdueLabel = t`Overdue`;

  const scheduledMaintenanceAlertsLink = buildFilteredPath(
    `${ROUTE_NAMES.alerts}?alertTypes=preventative_maintenance`,
  );
  const scheduledMaintenanceDueLink = buildFilteredPath(
    `${ROUTE_NAMES.alerts}?alertTypes=preventative_maintenance&scheduleMaintenanceStatus=DUE`,
  );
  const scheduledMaintenanceOverdueLink = buildFilteredPath(
    `${ROUTE_NAMES.alerts}?alertTypes=preventative_maintenance&scheduleMaintenanceStatus=OVERDUE`,
  );

  return (
    <Card
      text={t`Scheduled Maintenance`}
      buttonText={t`View All`}
      buttonAriaLabel={t`View All Assets with Scheduled Maintenance`}
      buttonDisabled={isViewAllDisabled}
      infoText={infoText}
      onButtonClick={onClickViewAll}
      marginTop={0}
    >
      {loading && <Loading />}
      {!loading && (
        <Flex flexDirection="row" marginBottom={1}>
          <Paper marginY={1}>
            <Grid>
              <Flex alignItems="top">
                <Badge
                  variant="square"
                  icon={Briefcase}
                  color={getBadgeColor(total)}
                />
              </Flex>
              <Flex flexDirection="column" marginLeft={1}>
                <H3 weight="regular">{getCountText(loading, total)}</H3>
                <LinkOrLabel
                  count={total}
                  to={scheduledMaintenanceAlertsLink}
                  text={scheduledMaintenanceAlertsLabel}
                />
              </Flex>
              <Divider
                css={`
                  height: auto;
                `}
                marginX={1}
              />
              <Flex flexDirection="column" marginLeft={1}>
                <H3 weight="regular">{getCountText(loading, due)}</H3>
                <LinkOrLabel
                  count={due}
                  to={scheduledMaintenanceDueLink}
                  text={scheduledMaintenanceDueLabel}
                />
              </Flex>
              <Flex flexDirection="column" marginLeft={1}>
                <H3 weight="regular">{getCountText(loading, overdue)}</H3>
                <LinkOrLabel
                  count={overdue}
                  to={scheduledMaintenanceOverdueLink}
                  text={scheduledMaintenanceOverdueLabel}
                />
              </Flex>
            </Grid>
          </Paper>
        </Flex>
      )}
    </Card>
  );
}
